import React from 'react';
import { Card, Row, Col, Badge, Typography, Skeleton, Alert, Avatar, List } from 'antd';
import {
  ShopOutlined,
  HomeOutlined,
  CarOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import { useAppSelector } from '../../hooks/redux';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { useGetOperationalStatusPollingQuery, useGetRealTimeMetricsPollingQuery } from '../../services/analyticsApi';
import TablesMapCard from './TablesMapCard';
import TopProductsCard from './TopProductsCard';
import { useMoneyPrivacy } from '../../hooks/useMoneyPrivacy';

const { Text } = Typography;

const OperationalStatus: React.FC = () => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { mask } = useMoneyPrivacy();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const { data: status, error, isLoading } = useGetOperationalStatusPollingQuery(
    currentRestaurant?.id || '',
    {
      skip: !currentRestaurant?.id,
      refetchOnMountOrArgChange: true,
      pollingInterval: 15000 // Poll every 15 seconds for operational status
    }
  );

  const { data: metrics } = useGetRealTimeMetricsPollingQuery(
    currentRestaurant?.id || '',
    {
      skip: !currentRestaurant?.id,
      refetchOnMountOrArgChange: true,
      pollingInterval: 10000 // Poll every 10 seconds for real-time metrics
    }
  );

  if (!currentRestaurant?.id) {
    return null;
  }

  if (error) {
    return (
      <Alert
        message="Error al cargar estado operacional"
        description="No se pudo cargar el estado operacional del restaurante"
        type="error"
        showIcon
        style={{ marginBottom: 24 }}
      />
    );
  }

  const formatTime = (time?: string) => {
    if (!time) return 'N/A';
    try {
      return formatInTimeZone(time, 'HH:mm');
    } catch {
      return 'N/A';
    }
  };

  // .NET serializa TimeSpan como "[-][d.]HH:mm:ss[.fffffff]"
  const formatDuration = (duration?: string) => {
    if (!duration) return 'N/A';
    const match = duration.match(/^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})/);
    if (!match) return 'N/A';
    const totalMinutes =
      (match[1] ? parseInt(match[1], 10) * 24 * 60 : 0) +
      parseInt(match[2], 10) * 60 +
      parseInt(match[3], 10);
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getOrderTypeIcon = (type: string) => {
    switch (type) {
      case 'DineIn':
        return <HomeOutlined />;
      case 'TakeOut':
        return <ShopOutlined />;
      case 'Delivery':
        return <CarOutlined />;
      default:
        return <ShopOutlined />;
    }
  };

  const getOrderTypeColor = (type: string) => {
    switch (type) {
      case 'DineIn':
        return 'var(--color-success)';
      case 'TakeOut':
        return 'var(--color-info)';
      case 'Delivery':
        return 'var(--color-warning)';
      default:
        return 'var(--color-neutral-text)';
    }
  };

  const ordersCard = isLoading || !status ? (
    <Card style={{ height: '100%' }}>
      <Skeleton active paragraph={{ rows: 3 }} />
    </Card>
  ) : (
    <Card
      title={
        <Badge
          status={status.isOpen ? 'processing' : 'error'}
          text={<span style={{ fontWeight: 600 }}>Órdenes por tipo</span>}
        />
      }
      extra={
        <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '13px' }}>
          {status.isOpen ? `Abierto desde ${formatTime(status.openedAt)}` : 'Cerrado'}
        </Text>
      }
      style={{ borderRadius: isMobile ? 8 : 12, height: '100%' }}
    >
      {metrics?.ordersByType && metrics.ordersByType.length > 0 ? (
        <List
          size="small"
          dataSource={metrics.ordersByType}
          renderItem={(orderType) => (
            <List.Item style={{ padding: '8px 0', border: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar
                  size="small"
                  style={{
                    backgroundColor: getOrderTypeColor(orderType.type),
                    marginRight: 8
                  }}
                  icon={getOrderTypeIcon(orderType.type)}
                />
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: 600 }}>
                    {orderType.displayName}
                  </Text>
                  {orderType.activeCount > 0 && (
                    <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
                      {orderType.activeCount} {orderType.activeCount === 1 ? 'activa' : 'activas'}
                    </Text>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Text strong style={{ fontSize: isMobile ? '12px' : '14px', display: 'block' }}>
                    {orderType.totalCount}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {mask('otros', `${currencySymbol} ${orderType.totalRevenue.toFixed(2)}`)}
                  </Text>
                </div>
              </div>
            </List.Item>
          )}
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Text type="secondary">Sin órdenes hoy</Text>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 8,
          borderTop: '1px solid var(--border-color)',
          paddingTop: 10,
        }}
      >
        <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
          {metrics?.ordersByType
            ? `Total: ${metrics.ordersByType.reduce((sum, o) => sum + o.totalCount, 0)} órdenes`
            : ''}
        </Text>
        <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
          Espera prom.: {formatDuration(status.averageWaitTime)}
        </Text>
      </div>
    </Card>
  );

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} lg={10}>
        <TablesMapCard />
      </Col>
      <Col xs={24} sm={12} lg={7}>
        {ordersCard}
      </Col>
      <Col xs={24} sm={12} lg={7}>
        <TopProductsCard />
      </Col>
    </Row>
  );
};

export default OperationalStatus;
